// React Query hooks for the helper API.
//
// `useHelperClient()` builds a `HelperClient` from the persisted credentials
// in the store; mutations (register/delete/reindex/search) invalidate the
// directories list so subsequent renders pick up the change. All hooks are
// no-ops when the helper isn't paired (the underlying queries stay disabled).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useHelperStore } from '../stores/helper';
import {
  type AnswerCitation,
  type AskRequest,
  type HelperClient,
  type RegisterDirectoryRequest,
  type SearchRequest,
  createHelperClient,
} from './helper';

export const helperQueryKeys = {
  status: ['helper', 'status'] as const,
  directories: ['helper', 'directories'] as const,
  excerpt: (fileId: string, chunkId: string) => ['helper', 'excerpt', fileId, chunkId] as const,
  search: (query: string, k: number, dirs: string[]) =>
    ['helper', 'search', query, k, dirs] as const,
  transcripts: ['helper', 'transcripts'] as const,
  transcript: (id: string) => ['helper', 'transcript', id] as const,
};

/** Returns a HelperClient if the helper is paired; null otherwise. */
export function useHelperClient(): HelperClient | null {
  const credentials = useHelperStore((s) => s.credentials);
  const setCredentials = useHelperStore((s) => s.setCredentials);
  return useMemo(() => {
    if (!credentials) return null;
    return createHelperClient({
      baseUrl: `http://127.0.0.1:${credentials.port}`,
      bearer: credentials.bearer,
      csrfToken: credentials.csrfToken,
      // When the helper picks a new ephemeral port across a restart, the
      // client auto-rediscovers and tells us; persist so subsequent client
      // instances start with the right port instead of failing once first.
      onPortChanged: (port) => setCredentials({ ...credentials, port }),
    });
  }, [credentials, setCredentials]);
}

export function useHelperStatus() {
  const client = useHelperClient();
  return useQuery({
    queryKey: helperQueryKeys.status,
    queryFn: () => {
      if (!client) throw new Error('helper not paired');
      return client.status();
    },
    enabled: !!client,
    refetchInterval: 5_000,
    // The helper goes offline often in dev (cargo restarts, port changes).
    // Default React Query behavior is to retry 3× then keep refetching every
    // 5s — leaving the UI in `loading` for ~15s, then briefly erroring,
    // then loading again. Cap retries and let `isError` surface so the
    // dashboard can show a useful "Helper unreachable" panel.
    retry: 1,
    retryDelay: 500,
  });
}

export function useDirectories() {
  const client = useHelperClient();
  return useQuery({
    queryKey: helperQueryKeys.directories,
    queryFn: () => {
      if (!client) throw new Error('helper not paired');
      return client.listDirectories();
    },
    enabled: !!client,
  });
}

export function useRegisterDirectory() {
  const client = useHelperClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: RegisterDirectoryRequest) => {
      if (!client) throw new Error('helper not paired');
      return client.registerDirectory(req);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: helperQueryKeys.directories });
      qc.invalidateQueries({ queryKey: helperQueryKeys.status });
    },
  });
}

export function useDeleteDirectory() {
  const client = useHelperClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!client) throw new Error('helper not paired');
      return client.deleteDirectory(id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: helperQueryKeys.directories });
      qc.invalidateQueries({ queryKey: helperQueryKeys.status });
    },
  });
}

export function useReindexDirectory() {
  const client = useHelperClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!client) throw new Error('helper not paired');
      return client.reindexDirectory(id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: helperQueryKeys.directories });
      qc.invalidateQueries({ queryKey: helperQueryKeys.status });
    },
  });
}

export function useSearch(req: SearchRequest, opts: { enabled?: boolean } = {}) {
  const client = useHelperClient();
  const k = req.k ?? 10;
  const dirs = req.directories ?? [];
  return useQuery({
    queryKey: helperQueryKeys.search(req.query, k, dirs),
    queryFn: () => {
      if (!client) throw new Error('helper not paired');
      return client.search(req);
    },
    enabled: !!client && (opts.enabled ?? !!req.query.trim()),
  });
}

/**
 * Run a question through the helper's orchestrator. Mutation rather than
 * query so the user controls when to spend an LLM call.
 */
export function useAsk() {
  const client = useHelperClient();
  return useMutation({
    mutationFn: (req: AskRequest) => {
      if (!client) throw new Error('helper not paired');
      return client.ask(req);
    },
  });
}

export function useTranscripts() {
  const client = useHelperClient();
  return useQuery({
    queryKey: helperQueryKeys.transcripts,
    queryFn: () => {
      if (!client) throw new Error('helper not paired');
      return client.listTranscripts();
    },
    enabled: !!client,
  });
}

export function useTranscript(id: string | undefined) {
  const client = useHelperClient();
  return useQuery({
    queryKey: id ? helperQueryKeys.transcript(id) : ['helper', 'transcript', 'none'],
    queryFn: () => {
      if (!client) throw new Error('helper not paired');
      if (!id) throw new Error('no id');
      return client.getTranscript(id);
    },
    enabled: !!client && !!id,
  });
}

export function useDeleteTranscript() {
  const client = useHelperClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!client) throw new Error('helper not paired');
      return client.deleteTranscript(id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: helperQueryKeys.transcripts });
    },
  });
}

export function useSummarizeTranscript() {
  const client = useHelperClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; force?: boolean }) => {
      if (!client) throw new Error('helper not paired');
      return client.summarizeTranscript(params.id, params.force ?? false);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: helperQueryKeys.transcripts });
      qc.invalidateQueries({ queryKey: helperQueryKeys.transcript(vars.id) });
    },
  });
}

/**
 * Streaming version of {@link useAsk}. Returns React state for citations,
 * the partial text accumulator, and lifecycle flags. Call `start` to begin
 * a new stream; previous streams are cancelled automatically.
 */
export function useAskStream() {
  const client = useHelperClient();
  const [citations, setCitations] = useState<AnswerCitation[]>([]);
  const [text, setText] = useState('');
  const [model, setModel] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    setCitations([]);
    setText('');
    setModel(null);
    setError(null);
  }, []);

  const start = useCallback(
    async (req: AskRequest) => {
      if (!client) {
        setError(new Error('helper not paired'));
        return;
      }
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      reset();
      setStreaming(true);
      try {
        await client.askStream(
          req,
          (ev) => {
            switch (ev.type) {
              case 'citations':
                setCitations(ev.citations);
                setModel(ev.model);
                break;
              case 'delta':
                setText((prev) => prev + ev.text);
                break;
              case 'error':
                setError(new Error(ev.message));
                break;
              case 'done':
                break;
            }
          },
          ctrl.signal,
        );
      } catch (e) {
        if ((e as { name?: string })?.name !== 'AbortError') setError(e);
      } finally {
        setStreaming(false);
      }
    },
    [client, reset],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    setStreaming(false);
  }, []);

  return { start, stop, reset, citations, text, model, streaming, error };
}
